import type { ReactNode } from 'react';
import type { BannerCard, BannerKind } from '../../shared/types';
import { GROUP_LABELS, KIND_ORDER, type GroupBy, type StatusFilter } from '../lib/collection';
import { KIND_LABELS } from '../lib/format';

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: 'Alle Status',
  pending: 'Offen',
  changes_requested: 'Änderungen gewünscht',
  approved: 'Freigegeben',
};

/** Ansicht einer Motiv-Sammlung: gruppieren, nach Typ ein- und ausblenden, nach Status filtern. */
export function CollectionBar({
  banners,
  groupBy,
  onGroupBy,
  hidden,
  onHidden,
  status,
  onStatus,
  children,
}: {
  banners: BannerCard[];
  groupBy: GroupBy;
  onGroupBy: (value: GroupBy) => void;
  /** Ausgeblendete Typen – standardmäßig ist alles sichtbar. */
  hidden: BannerKind[];
  onHidden: (value: BannerKind[]) => void;
  status: StatusFilter;
  onStatus: (value: StatusFilter) => void;
  children?: ReactNode;
}) {
  const kindCounts = KIND_ORDER.map((kind) => ({
    kind,
    count: banners.filter((b) => b.kind === kind).length,
  })).filter((k) => k.count > 0);
  const statusCount = (s: StatusFilter) =>
    s === 'all' ? banners.length : banners.filter((b) => b.status === s).length;
  const toggleKind = (kind: BannerKind) =>
    onHidden(hidden.includes(kind) ? hidden.filter((k) => k !== kind) : [...hidden, kind]);

  return (
    <div className="collection-bar">
      <div className="segmented" role="group" aria-label="Gruppieren nach">
        {(Object.keys(GROUP_LABELS) as GroupBy[]).map((value) => (
          <button key={value} aria-pressed={groupBy === value} onClick={() => onGroupBy(value)}>
            {GROUP_LABELS[value]}
          </button>
        ))}
      </div>
      {kindCounts.length > 1 && (
        <div className="kind-chips" role="group" aria-label="Typen einblenden">
          {kindCounts.map(({ kind, count }) => (
            <button
              key={kind}
              className="kind-chip"
              aria-pressed={!hidden.includes(kind)}
              onClick={() => toggleKind(kind)}
              title={hidden.includes(kind) ? 'Einblenden' : 'Ausblenden'}
            >
              {KIND_LABELS[kind]} <span className="kind-count">{count}</span>
            </button>
          ))}
          {hidden.length > 0 && (
            <button className="text-link small" onClick={() => onHidden([])}>
              Alle zeigen
            </button>
          )}
        </div>
      )}
      <select
        className="select select-sm"
        value={status}
        onChange={(e) => onStatus(e.target.value as StatusFilter)}
        aria-label="Nach Status filtern"
      >
        {(Object.keys(STATUS_LABELS) as StatusFilter[]).map((s) => (
          <option key={s} value={s} disabled={s !== 'all' && statusCount(s) === 0}>
            {STATUS_LABELS[s]} ({statusCount(s)})
          </option>
        ))}
      </select>
      <span className="spacer" />
      {children}
    </div>
  );
}
